import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import toast, { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import {
  WORKFLOW,
  getWorkflowStatus,
  slaBand,
  slaBandClass,
} from "../utils/pendingOrderWorkflow";
import { getVisibleAppBranches, normalizeBranchValue } from "../config/appBranches";

function formatOrderDate(ts) {
  if (ts == null) return "—";
  const d = typeof ts.toDate === "function" ? ts.toDate() : null;
  if (!d || isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function tsFromInputDate(d) {
  if (!d) return null;
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Timestamp.fromDate(x);
}

function dateInputValue(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseTsToDate(v) {
  if (v == null) return null;
  if (typeof v.toDate === "function") {
    const d = v.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  return null;
}

function toDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") {
    const d = v.toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d : null;
  }
  if (v instanceof Date) return !isNaN(v.getTime()) ? v : null;
  if (typeof v === "number") return !isNaN(new Date(v).getTime()) ? new Date(v) : null;
  if (typeof v === "string") {
    const d = new Date(v);
    return !isNaN(d.getTime()) ? d : null;
  }
  return null;
}

function getFieldString(obj, keys, fallback = "") {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim()) return String(v).trim();
  }
  return fallback;
}

function ymd(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isCancelledDoc(data) {
  return String(data?.status ?? "").toLowerCase().includes("cancel");
}

/** Workflow label for display (cancelled overrides inferred draft). */
function displayWorkflow(data) {
  if (isCancelledDoc(data)) return "cancelled";
  return getWorkflowStatus(data);
}

const WORKFLOW_FILTER = [
  { value: "", label: "All statuses" },
  { value: WORKFLOW.DRAFT, label: "Draft" },
  { value: WORKFLOW.SUBMITTED, label: "Submitted" },
  { value: WORKFLOW.APPROVED, label: "Approved" },
  { value: WORKFLOW.ORDERED, label: "Ordered" },
  { value: WORKFLOW.RECEIVED, label: "Received" },
  { value: "cancelled", label: "Cancelled" },
];

const SORT_OPTIONS = [
  { value: "date", label: "Order date (newest)" },
  { value: "dateAsc", label: "Order date (oldest)" },
  { value: "aging", label: "Days open (high)" },
  { value: "amount", label: "Amount (high)" },
  { value: "supplier", label: "Supplier (A–Z)" },
];

export default function OrderReportsPage() {
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const visibleBranches = useMemo(() => getVisibleAppBranches(storedUser), [storedUser]);
  const branchFilterOptions = useMemo(
    () => [{ value: "", label: "All branches" }, ...visibleBranches.map((b) => ({ value: b.value, label: b.label }))],
    [visibleBranches]
  );

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const today = new Date();
  const defaultTo = ymd(today);
  const defaultFrom = ymd(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 90));
  const [filterFrom, setFilterFrom] = useState(defaultFrom);
  const [filterTo, setFilterTo] = useState(defaultTo);
  const [branchFilter, setBranchFilter] = useState("");
  const [workflowFilter, setWorkflowFilter] = useState("");
  const [sortBy, setSortBy] = useState("date");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Read-only "full details" panel, opened by clicking a row.
  const [detailsId, setDetailsId] = useState(null);
  const [form, setForm] = useState({
    invoiceNo: "",
    supplier: "",
    brand: "",
    amount: "",
    quantity: "",
    remarks: "",
    status: "pending",
    branch: "Main",
    workflowStatus: WORKFLOW.SUBMITTED,
    approverEmail: "",
    linkedPoRef: "",
    linkedGrnRef: "",
    orderPreparedBy: "",
    orderDate: null,
    receivedDate: null,
  });

  useEffect(() => {
    if (!branchFilter) return;
    const allowed = new Set(visibleBranches.map((b) => b.value));
    if (!allowed.has(branchFilter)) setBranchFilter("");
  }, [visibleBranches, branchFilter]);

  useEffect(() => {
    if (!visibleBranches.length) return;
    const allowed = new Set(visibleBranches.map((b) => b.value));
    setForm((f) => (allowed.has(f.branch) ? f : { ...f, branch: visibleBranches[0].value }));
  }, [visibleBranches]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "orders"), (snap) => {
      const list = [...snap.docs]
        .sort((a, b) => {
          const ta = a.data()?.createdAt?.toDate?.()?.getTime?.() ?? 0;
          const tb = b.data()?.createdAt?.toDate?.()?.getTime?.() ?? 0;
          return tb - ta;
        })
        .slice(0, 2500);
      setDocs(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const rows = useMemo(() => {
    const out = [];
    for (const d of docs) {
      const data = d.data() || {};
      const branch = normalizeBranchValue(data.branch);
      const orderDate =
        toDate(data.date ?? data.orderDate ?? data.createdAt) ?? new Date(data.createdAt?.seconds * 1000 || Date.now());
      const cancelled = isCancelledDoc(data);
      const wf = displayWorkflow(data);
      const orderYmd = ymd(orderDate);
      if (filterFrom && orderYmd && orderYmd < filterFrom) continue;
      if (filterTo && orderYmd && orderYmd > filterTo) continue;
      if (branchFilter && branch !== branchFilter) continue;
      if (workflowFilter && wf !== workflowFilter) continue;

      const q = search.trim().toLowerCase();
      if (q) {
        const invoice = String(data.invoiceNo ?? "").toLowerCase();
        const supplier = String(data.supplier ?? "").toLowerCase();
        const brand = String(data.brand ?? "").toLowerCase();
        const po = String(data.linkedPoRef ?? data.linkedPo ?? "").toLowerCase();
        const grn = String(data.linkedGrnRef ?? data.linkedGrn ?? "").toLowerCase();
        const appr = String(data.approverEmail ?? "").toLowerCase();
        if (
          !invoice.includes(q) &&
          !supplier.includes(q) &&
          !brand.includes(q) &&
          !po.includes(q) &&
          !grn.includes(q) &&
          !appr.includes(q)
        ) {
          continue;
        }
      }

      const terminal = cancelled || wf === WORKFLOW.RECEIVED;
      const pendingDays = terminal
        ? null
        : Math.max(0, Math.floor((Date.now() - orderDate.getTime()) / (86400 * 1000)));
      const band = pendingDays == null ? null : slaBand(pendingDays);

      // Accept either `amount` (legacy / canonical) or `orderAmount` (newer
      // explicit name written by the Pending Orders page).
      const rawAmount = data.amount ?? data.orderAmount;
      const amount =
        typeof rawAmount === "number"
          ? rawAmount
          : parseFloat(String(rawAmount ?? "")) || 0;
      out.push({
        id: d.id,
        data,
        branch,
        orderDate,
        wf,
        cancelled,
        pendingDays,
        sla: band,
        amount,
        invoiceNo: getFieldString(data, ["invoiceNo", "invoiceNumber"], "—"),
        supplier: getFieldString(data, ["supplier", "party", "vendor"], "—"),
        brand: getFieldString(data, ["brand"], "—"),
        qty: data.quantity ?? "—",
        preparedBy: getFieldString(data, ["orderPreparedBy", "preparedBy", "prepared_by"], "—"),
        approverEmail: getFieldString(data, ["approverEmail"], ""),
        linkedPo: getFieldString(data, ["linkedPoRef", "linkedPo", "purchaseOrderRef"], ""),
        linkedGrn: getFieldString(data, ["linkedGrnRef", "linkedGrn", "grnRef"], ""),
      });
    }

    out.sort((a, b) => {
      switch (sortBy) {
        case "dateAsc":
          return a.orderDate.getTime() - b.orderDate.getTime();
        case "aging":
          return (b.pendingDays ?? -1) - (a.pendingDays ?? -1);
        case "amount":
          return b.amount - a.amount;
        case "supplier":
          return a.supplier.localeCompare(b.supplier, undefined, { sensitivity: "base" });
        case "date":
        default:
          return b.orderDate.getTime() - a.orderDate.getTime();
      }
    });

    return out;
  }, [docs, search, filterFrom, filterTo, branchFilter, workflowFilter, sortBy]);

  const summary = useMemo(() => {
    let totalAmt = 0;
    const byWf = {};
    for (const r of rows) {
      totalAmt += r.amount || 0;
      const k = r.wf;
      byWf[k] = (byWf[k] || 0) + 1;
    }
    return { count: rows.length, totalAmt, byWf };
  }, [rows]);

  const openAdd = () => {
    setEditingId(null);
    setForm({
      invoiceNo: "",
      supplier: "",
      brand: "",
      amount: "",
      quantity: "",
      remarks: "",
      status: "pending",
      branch: "Main",
      workflowStatus: WORKFLOW.SUBMITTED,
      approverEmail: "",
      linkedPoRef: "",
      linkedGrnRef: "",
      orderPreparedBy: "",
      orderDate: new Date(),
      receivedDate: null,
    });
    setModalOpen(true);
  };

  const openEdit = (docSnap) => {
    const data = docSnap.data() || {};
    setEditingId(docSnap.id);
    // Prefer the new `orderAmount` field; fall back to legacy `amount`.
    const amt = data.orderAmount ?? data.amount;
    const qty = data.quantity;
    setForm({
      invoiceNo: String(data.invoiceNo ?? ""),
      supplier: String(data.supplier ?? ""),
      brand: String(data.brand ?? ""),
      amount: amt == null || amt === "" ? "" : String(amt),
      quantity: qty == null || qty === "" ? "" : String(qty),
      remarks: String(data.remarks ?? ""),
      status: String(data.status ?? "pending"),
      branch: normalizeBranchValue(data.branch),
      workflowStatus: isCancelledDoc(data) ? WORKFLOW.SUBMITTED : getWorkflowStatus(data),
      approverEmail: getFieldString(data, ["approverEmail"], ""),
      linkedPoRef: getFieldString(data, ["linkedPoRef", "linkedPo", "purchaseOrderRef"], ""),
      linkedGrnRef: getFieldString(data, ["linkedGrnRef", "linkedGrn", "grnRef"], ""),
      orderPreparedBy: getFieldString(data, ["orderPreparedBy", "preparedBy", "prepared_by"], ""),
      orderDate: parseTsToDate(data.date) ?? toDate(data.orderDate) ?? toDate(data.createdAt),
      receivedDate: parseTsToDate(data.receivedDate),
    });
    setModalOpen(true);
  };

  const saveOrder = async () => {
    const amount = parseFloat(String(form.amount).trim()) || 0;
    const quantity = parseFloat(String(form.quantity).trim()) || 0;
    const branch = normalizeBranchValue(form.branch);
    const wf = String(form.workflowStatus || "").toLowerCase();
    const legacy = String(form.status || "").trim().toLowerCase();

    const payload = {
      invoiceNo: form.invoiceNo.trim(),
      supplier: form.supplier.trim(),
      brand: form.brand.trim(),
      orderAmount: amount,
      quantity,
      remarks: form.remarks.trim(),
      branch,
      workflowStatus: wf,
      approverEmail: form.approverEmail.trim() || deleteField(),
      linkedPoRef: form.linkedPoRef.trim() || deleteField(),
      linkedGrnRef: form.linkedGrnRef.trim() || deleteField(),
      orderPreparedBy: form.orderPreparedBy.trim() || deleteField(),
      date: form.orderDate != null ? tsFromInputDate(form.orderDate) : serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    if (legacy === "cancelled") {
      payload.status = "cancelled";
    } else if (wf === WORKFLOW.RECEIVED) {
      payload.status = "received";
      payload.orderReceived = true;
      payload.receivedDate = form.receivedDate != null ? tsFromInputDate(form.receivedDate) : serverTimestamp();
    } else if (wf === WORKFLOW.APPROVED) {
      payload.status = "approved";
      payload.orderReceived = false;
    } else if (wf === WORKFLOW.ORDERED) {
      payload.status = "ordered";
      payload.orderReceived = false;
    } else if (wf === WORKFLOW.SUBMITTED) {
      payload.status = "pending";
      payload.orderReceived = false;
    } else if (wf === WORKFLOW.DRAFT) {
      payload.status = legacy || "pending";
      payload.orderReceived = false;
    } else {
      payload.status = legacy || "pending";
    }

    if (wf !== WORKFLOW.RECEIVED && editingId) {
      payload.receivedDate = deleteField();
    }

    try {
      if (editingId) {
        await updateDoc(doc(db, "orders", editingId), payload);
        toast.success("Order updated");
      } else {
        await addDoc(collection(db, "orders"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        toast.success("Order added");
      }
      setModalOpen(false);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || String(e));
    }
  };

  const removeOrder = async (id) => {
    if (!window.confirm("Delete this order? This cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, "orders", id));
      toast.success("Order deleted");
    } catch (e) {
      toast.error(e?.message || String(e));
    }
  };

  const exportCsv = () => {
    if (!rows.length) return toast.error("Nothing to export");
    const header = [
      "orderDate",
      "branch",
      "invoiceNo",
      "supplier",
      "brand",
      "quantity",
      "amount",
      "workflow",
      "daysOpen",
      "approver",
      "linkedPo",
      "linkedGrn",
      "preparedBy",
      "legacyStatus",
    ];
    const lines = [header.join(",")].concat(
      rows.map((r) =>
        [
          ymd(r.orderDate),
          r.branch,
          r.invoiceNo,
          r.supplier,
          r.brand,
          r.qty,
          r.amount,
          r.wf,
          r.pendingDays ?? "",
          r.approverEmail,
          r.linkedPo,
          r.linkedGrn,
          r.preparedBy,
          String(r.data?.status ?? ""),
        ]
          .map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `order-report-${filterFrom || "all"}-${filterTo || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV downloaded");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="max-w-[1400px] mx-auto p-4 pb-24">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Order reports</h1>
            <p className="text-sm text-slate-600 mt-0.5">
              Filtered view of purchase orders — workflow, SLA, and traceability. Prefer{" "}
              <Link to="/pending-orders" className="text-indigo-600 font-medium hover:underline">
                Pending orders
              </Link>{" "}
              for day-to-day entry and approvals.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={exportCsv}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={openAdd}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
            >
              Quick add
            </button>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
          <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <p className="text-xs font-medium text-slate-500">Rows (after filters)</p>
            <p className="text-2xl font-bold text-slate-900">{summary.count}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <p className="text-xs font-medium text-slate-500">Amount (filtered)</p>
            <p className="text-2xl font-bold text-slate-900">₹{summary.totalAmt.toFixed(2)}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:col-span-2">
            <p className="text-xs font-medium text-slate-500 mb-1">By workflow</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(summary.byWf)
                .sort((a, b) => b[1] - a[1])
                .map(([k, n]) => (
                  <span
                    key={k}
                    className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-800"
                  >
                    {k}: {n}
                  </span>
                ))}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm mb-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block text-xs">
              <span className="text-slate-600 font-medium">From</span>
              <input
                type="date"
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
              />
            </label>
            <label className="block text-xs">
              <span className="text-slate-600 font-medium">To</span>
              <input
                type="date"
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
              />
            </label>
            <label className="block text-xs">
              <span className="text-slate-600 font-medium">Branch</span>
              <select
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
              >
                {branchFilterOptions.map((b) => (
                  <option key={b.value || "all"} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs">
              <span className="text-slate-600 font-medium">Workflow</span>
              <select
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                value={workflowFilter}
                onChange={(e) => setWorkflowFilter(e.target.value)}
              >
                {WORKFLOW_FILTER.map((w) => (
                  <option key={w.value || "all"} value={w.value}>
                    {w.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="bg-slate-50 rounded-lg border border-slate-100 px-3 py-2 flex items-center gap-2 flex-1 min-w-0">
              <svg className="w-5 h-5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="search"
                className="flex-1 text-sm outline-none bg-transparent min-w-0"
                placeholder="Search invoice, supplier, brand, PO, GRN, approver…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search ? (
                <button type="button" className="text-slate-500 p-1 shrink-0" onClick={() => setSearch("")} aria-label="Clear">
                  ×
                </button>
              ) : null}
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600 whitespace-nowrap">
              Sort
              <select
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm font-medium text-slate-800"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {SORT_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-center text-slate-500 py-16 text-sm rounded-xl border border-dashed border-slate-200 bg-white">
            No orders match the current filters.
          </p>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    <th className="px-3 py-2 whitespace-nowrap">Date</th>
                    <th className="px-3 py-2 whitespace-nowrap">Branch</th>
                    <th className="px-3 py-2 whitespace-nowrap">Invoice</th>
                    <th className="px-3 py-2 min-w-[120px]">Supplier</th>
                    <th className="px-3 py-2">Brand</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Qty</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Amount</th>
                    <th className="px-3 py-2 whitespace-nowrap">Workflow</th>
                    <th className="px-3 py-2 whitespace-nowrap">Open</th>
                    <th className="px-3 py-2 min-w-[100px]">Approver</th>
                    <th className="px-3 py-2">PO / GRN</th>
                    <th className="px-3 py-2 whitespace-nowrap">Prepared</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => {
                    const bandClass = r.sla ? slaBandClass(r.sla) : "bg-slate-50 text-slate-500 border-slate-200";
                    return (
                      <tr
                        key={r.id}
                        className="hover:bg-indigo-50/60 cursor-pointer"
                        onClick={() => setDetailsId(r.id)}
                        title="Click for full details"
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-slate-800">{ymd(r.orderDate)}</td>
                        <td className="px-3 py-2 text-slate-700">{r.branch}</td>
                        <td className="px-3 py-2 font-medium text-slate-900">{r.invoiceNo}</td>
                        <td className="px-3 py-2 text-slate-800 max-w-[200px] truncate" title={r.supplier}>
                          {r.supplier}
                        </td>
                        <td className="px-3 py-2 text-slate-700 max-w-[120px] truncate" title={r.brand}>
                          {r.brand}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.qty}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">₹{r.amount.toFixed(2)}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-xs font-semibold capitalize text-slate-800">
                            {r.wf}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {r.pendingDays != null ? (
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${bandClass}`}>
                              {r.pendingDays}d
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 max-w-[140px] truncate" title={r.approverEmail}>
                          {r.approverEmail || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">
                          <div className="flex flex-col gap-0.5">
                            {r.linkedPo ? <span>PO: {r.linkedPo}</span> : null}
                            {r.linkedGrn ? <span>GRN: {r.linkedGrn}</span> : null}
                            {!r.linkedPo && !r.linkedGrn ? "—" : null}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-700 text-xs">{r.preparedBy}</td>
                        <td
                          className="px-3 py-2 text-right whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="p-1.5 text-slate-600 hover:bg-slate-100 rounded inline-flex"
                            onClick={() => setDetailsId(r.id)}
                            title="View full details"
                            aria-label="View"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5C21.27 7.61 17 4.5 12 4.5zm0 12.5a5 5 0 110-10 5 5 0 010 10zm0-8a3 3 0 100 6 3 3 0 000-6z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded inline-flex"
                            onClick={() => {
                              const snap = docs.find((x) => x.id === r.id);
                              if (snap) openEdit(snap);
                              else toast.error("Order not found in loaded list");
                            }}
                            title="Edit"
                            aria-label="Edit"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="p-1.5 text-rose-600 hover:bg-rose-50 rounded inline-flex"
                            onClick={() => removeOrder(r.id)}
                            title="Delete"
                            aria-label="Delete"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[1300] bg-black/40 flex items-center justify-center p-3"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-base font-bold text-gray-900 mb-3">{editingId ? "Edit order" : "Quick add order"}</h2>
            <div className="space-y-2 text-sm">
              {[
                ["invoiceNo", "Invoice No"],
                ["supplier", "Supplier"],
                ["brand", "Brand"],
                ["amount", "Amount", "decimal"],
                ["quantity", "Quantity", "decimal"],
                ["remarks", "Remarks"],
              ].map(([key, label, inputMode]) => (
                <label key={key} className="block">
                  <span className="text-xs text-gray-600">{label}</span>
                  <input
                    className="w-full border rounded-lg px-2 py-1.5 mt-0.5 border-slate-200"
                    value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    inputMode={inputMode || "text"}
                  />
                </label>
              ))}

              <label className="block">
                <span className="text-xs text-gray-600">Branch</span>
                <select
                  className="w-full border rounded-lg px-2 py-1.5 mt-0.5 border-slate-200"
                  value={form.branch}
                  onChange={(e) => setForm((f) => ({ ...f, branch: e.target.value }))}
                >
                  {branchFilterOptions.filter((b) => b.value).map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-xs text-gray-600">Workflow</span>
                <select
                  className="w-full border rounded-lg px-2 py-1.5 mt-0.5 border-slate-200"
                  value={form.workflowStatus}
                  onChange={(e) => setForm((f) => ({ ...f, workflowStatus: e.target.value }))}
                >
                  <option value={WORKFLOW.DRAFT}>draft</option>
                  <option value={WORKFLOW.SUBMITTED}>submitted</option>
                  <option value={WORKFLOW.APPROVED}>approved</option>
                  <option value={WORKFLOW.ORDERED}>ordered</option>
                  <option value={WORKFLOW.RECEIVED}>received</option>
                </select>
              </label>

              <label className="block">
                <span className="text-xs text-gray-600">Legacy status (stored as status)</span>
                <select
                  className="w-full border rounded-lg px-2 py-1.5 mt-0.5 border-slate-200"
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  {["pending", "approved", "ordered", "received", "cancelled"].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-xs text-gray-600">Prepared by</span>
                <input
                  className="w-full border rounded-lg px-2 py-1.5 mt-0.5 border-slate-200"
                  value={form.orderPreparedBy}
                  onChange={(e) => setForm((f) => ({ ...f, orderPreparedBy: e.target.value }))}
                  placeholder="Name"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-600">Approver email</span>
                <input
                  type="email"
                  className="w-full border rounded-lg px-2 py-1.5 mt-0.5 border-slate-200"
                  value={form.approverEmail}
                  onChange={(e) => setForm((f) => ({ ...f, approverEmail: e.target.value }))}
                  placeholder="optional"
                />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-xs text-gray-600">Linked PO</span>
                  <input
                    className="w-full border rounded-lg px-2 py-1.5 mt-0.5 border-slate-200"
                    value={form.linkedPoRef}
                    onChange={(e) => setForm((f) => ({ ...f, linkedPoRef: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-600">Linked GRN</span>
                  <input
                    className="w-full border rounded-lg px-2 py-1.5 mt-0.5 border-slate-200"
                    value={form.linkedGrnRef}
                    onChange={(e) => setForm((f) => ({ ...f, linkedGrnRef: e.target.value }))}
                  />
                </label>
              </div>

              <div className="flex flex-wrap items-center gap-2 py-1">
                <span className="text-xs text-gray-700 flex-1">
                  Order date:{" "}
                  {form.orderDate == null ? "Not set" : formatOrderDate(tsFromInputDate(form.orderDate))}
                </span>
                <input
                  type="date"
                  className="border rounded-lg px-2 py-1 text-xs border-slate-200"
                  value={dateInputValue(form.orderDate)}
                  min="2000-01-01"
                  max="2100-12-31"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) {
                      setForm((f) => ({ ...f, orderDate: null }));
                      return;
                    }
                    const [y, m, d] = v.split("-").map(Number);
                    setForm((f) => ({ ...f, orderDate: new Date(y, m - 1, d) }));
                  }}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 py-1">
                <span className="text-xs text-gray-700 flex-1">
                  Received date:{" "}
                  {form.receivedDate == null ? "Not set" : formatOrderDate(tsFromInputDate(form.receivedDate))}
                </span>
                <input
                  type="date"
                  className="border rounded-lg px-2 py-1 text-xs border-slate-200"
                  value={dateInputValue(form.receivedDate)}
                  min="2000-01-01"
                  max="2100-12-31"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) {
                      setForm((f) => ({ ...f, receivedDate: null }));
                      return;
                    }
                    const [y, m, d] = v.split("-").map(Number);
                    setForm((f) => ({ ...f, receivedDate: new Date(y, m - 1, d) }));
                  }}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4 pt-2 border-t border-slate-100">
              <button type="button" className="px-3 py-1.5 rounded-lg border text-sm border-slate-200" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold"
                onClick={saveOrder}
              >
                {editingId ? "Update" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailsId
        ? (() => {
            const snap = docs.find((d) => d.id === detailsId);
            if (!snap) return null;
            const data = snap.data() || {};
            const row = rows.find((r) => r.id === detailsId);
            const orderDate =
              toDate(data.date ?? data.orderDate ?? data.createdAt) ?? null;
            const receivedDate = toDate(data.receivedDate);
            const createdAt = toDate(data.createdAt);
            const updatedAt = toDate(data.updatedAt);
            const cancelled = isCancelledDoc(data);
            const wf = displayWorkflow(data);
            const days = row?.pendingDays;
            const band = row?.sla ? slaBandClass(row.sla) : "bg-slate-50 text-slate-500 border-slate-200";

            // Friendly section: the "core" fields we know about.
            const KNOWN = new Set([
              "invoiceNo", "invoiceNumber", "supplier", "party", "vendor", "brand",
              "amount", "quantity", "remarks", "branch", "status", "workflowStatus",
              "approverEmail", "linkedPoRef", "linkedPo", "purchaseOrderRef",
              "linkedGrnRef", "linkedGrn", "grnRef",
              "orderPreparedBy", "preparedBy", "prepared_by",
              "date", "orderDate", "receivedDate", "createdAt", "updatedAt",
              "orderReceived",
            ]);
            const extras = Object.entries(data)
              .filter(([k]) => !KNOWN.has(k))
              .sort(([a], [b]) => a.localeCompare(b));

            const formatValue = (v) => {
              if (v == null || v === "") return "—";
              if (typeof v?.toDate === "function") {
                const d = v.toDate();
                return isNaN(d.getTime())
                  ? String(v)
                  : d.toLocaleString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    });
              }
              if (v instanceof Date) return v.toLocaleString("en-IN");
              if (typeof v === "boolean") return v ? "Yes" : "No";
              if (typeof v === "object") return JSON.stringify(v);
              return String(v);
            };

            const Field = ({ label, value, mono = false, wide = false }) => (
              <div className={wide ? "sm:col-span-2" : ""}>
                <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                  {label}
                </dt>
                <dd className={`text-sm text-slate-900 mt-0.5 ${mono ? "font-mono" : ""} break-words`}>
                  {value ?? "—"}
                </dd>
              </div>
            );

            return (
              <div
                className="fixed inset-0 z-[1300] bg-black/50 flex items-center justify-center p-3"
                onClick={() => setDetailsId(null)}
              >
                <div
                  className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex flex-wrap items-start justify-between gap-3 px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
                    <div className="min-w-0">
                      <h2 className="text-base font-bold text-slate-900 truncate">
                        {data.invoiceNo || "(no invoice no)"}{" "}
                        <span className="font-normal text-slate-500">· {data.supplier || "—"}</span>
                      </h2>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        <span className="inline-flex rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-semibold capitalize text-slate-800">
                          {wf}
                        </span>
                        {cancelled ? (
                          <span className="inline-flex rounded-md border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[11px] font-semibold text-rose-700">
                            cancelled
                          </span>
                        ) : null}
                        {days != null ? (
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${band}`}>
                            {days}d open
                          </span>
                        ) : null}
                        <span className="text-[11px] text-slate-500">
                          ID: <span className="font-mono">{snap.id}</span>
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-slate-500 hover:bg-slate-200 rounded p-1"
                      onClick={() => setDetailsId(null)}
                      aria-label="Close"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                      </svg>
                    </button>
                  </div>

                  <div className="p-5 space-y-5">
                    {/* Core */}
                    <section>
                      <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
                        Order
                      </h3>
                      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                        <Field label="Invoice No" value={data.invoiceNo || "—"} />
                        <Field label="Branch" value={normalizeBranchValue(data.branch)} />
                        <Field label="Supplier" value={data.supplier} wide />
                        <Field label="Brand" value={data.brand} />
                        <Field label="Quantity" value={data.quantity ?? "—"} />
                        <Field
                          label="Amount"
                          value={(() => {
                            const v = data.orderAmount ?? data.amount;
                            const n = typeof v === "number" ? v : parseFloat(String(v ?? "")) || 0;
                            return `₹${n.toFixed(2)}`;
                          })()}
                        />
                        <Field label="Legacy status" value={data.status} />
                        <Field label="Remarks" value={data.remarks} wide />
                      </dl>
                    </section>

                    {/* Dates */}
                    <section>
                      <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
                        Timeline
                      </h3>
                      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                        <Field
                          label="Order date"
                          value={orderDate ? orderDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                        />
                        <Field
                          label="Received date"
                          value={receivedDate ? receivedDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                        />
                        <Field
                          label="Created"
                          value={createdAt ? createdAt.toLocaleString("en-IN") : "—"}
                        />
                        <Field
                          label="Last updated"
                          value={updatedAt ? updatedAt.toLocaleString("en-IN") : "—"}
                        />
                      </dl>
                    </section>

                    {/* Workflow & references */}
                    <section>
                      <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
                        Workflow & references
                      </h3>
                      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                        <Field label="Workflow status" value={wf} />
                        <Field
                          label="Order received"
                          value={data.orderReceived === true ? "Yes" : data.orderReceived === false ? "No" : "—"}
                        />
                        <Field label="Prepared by" value={data.orderPreparedBy || data.preparedBy || data.prepared_by} />
                        <Field label="Approver email" value={data.approverEmail} />
                        <Field
                          label="Linked PO"
                          value={data.linkedPoRef || data.linkedPo || data.purchaseOrderRef}
                          mono
                        />
                        <Field
                          label="Linked GRN"
                          value={data.linkedGrnRef || data.linkedGrn || data.grnRef}
                          mono
                        />
                      </dl>
                    </section>

                    {/* Anything else stored on the doc */}
                    {extras.length ? (
                      <section>
                        <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
                          Other fields ({extras.length})
                        </h3>
                        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                          {extras.map(([k, v]) => (
                            <Field key={k} label={k} value={formatValue(v)} mono={typeof v !== "object"} />
                          ))}
                        </dl>
                      </section>
                    ) : null}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
                    <button
                      type="button"
                      className="px-3 py-1.5 rounded-lg border text-sm border-slate-200 bg-white hover:bg-slate-50"
                      onClick={() => setDetailsId(null)}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700"
                      onClick={async () => {
                        const id = snap.id;
                        setDetailsId(null);
                        await removeOrder(id);
                      }}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
                      onClick={() => {
                        setDetailsId(null);
                        openEdit(snap);
                      }}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            );
          })()
        : null}
    </div>
  );
}
